import React, { useState } from 'react';
import { Form, Input, Button, Card, Tabs, message, Checkbox } from 'antd';
import { UserOutlined, LockOutlined, MobileOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { passwordLogin, phoneLogin, sendSmsCode } from '../services/adminApi';
import { APP_CONFIG } from '../config';

interface LoginFormData {
  username: string;
  password: string;
}

interface PhoneLoginFormData {
  phone: string;
  smsCode: string;
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [passwordForm] = Form.useForm<LoginFormData>();
  const [phoneForm] = Form.useForm<PhoneLoginFormData>();
  const navigate = useNavigate();

  // 密码登录
  const handlePasswordLogin = async (values: LoginFormData) => {
    setLoading(true);
    try {
      const response = await passwordLogin(values.username, values.password);
      
      console.log('[Login] 响应数据:', response);
      
      if (response.data.success) {
        const { user, accessToken, refreshToken } = response.data.data;
        
        console.log('[Login] 用户信息:', user);
        console.log('[Login] Token:', accessToken);
        
        // 保存token和用户信息
        localStorage.setItem(APP_CONFIG.tokenKey, accessToken);
        localStorage.setItem(APP_CONFIG.refreshTokenKey, refreshToken);
        localStorage.setItem(APP_CONFIG.userKey, JSON.stringify(user));
        
        console.log('[Login] 存储完成，准备跳转');
        message.success('登录成功');

        // 延迟跳转，确保存储完成
        setTimeout(() => {
          console.log('[Login] 执行跳转到 /dashboard');
          window.location.href = '/dashboard';
        }, 100);
      }
    } catch (error: any) {
      console.error('[Login] 登录错误:', error);
      message.error(error.message || '登录失败，请检查用户名和密码');
    } finally {
      setLoading(false);
    }
  };

  // 发送短信验证码
  const handleSendSms = async () => {
    const phone = phoneForm.getFieldValue('phone');
    
    if (!phone) {
      message.warning('请先输入手机号');
      return;
    }
    
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      message.warning('请输入正确的手机号');
      return;
    }
    
    setSmsLoading(true);
    try {
      const response = await sendSmsCode(phone);
      
      if (response.data.success) {
        message.success('验证码已发送');
        
        // 开发环境显示验证码
        if (response.data.data?.code) {
          message.info(`开发环境验证码: ${response.data.data.code}`);
        }
        
        // 开始倒计时
        setSmsCountdown(60);
        const timer = setInterval(() => {
          setSmsCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error: any) {
      message.error(error.message || '发送验证码失败');
    } finally {
      setSmsLoading(false);
    }
  };

  // 手机号登录
  const handlePhoneLogin = async (values: PhoneLoginFormData) => {
    setLoading(true);
    try {
      const response = await phoneLogin(values.phone, values.smsCode);
      
      if (response.data.success) {
        const { user, accessToken, refreshToken } = response.data.data;
        
        // 保存token和用户信息
        localStorage.setItem(APP_CONFIG.tokenKey, accessToken);
        localStorage.setItem(APP_CONFIG.refreshTokenKey, refreshToken);
        localStorage.setItem(APP_CONFIG.userKey, JSON.stringify(user));
        
        message.success('登录成功');
        navigate('/dashboard');
      }
    } catch (error: any) {
      message.error(error.message || '登录失败，请检查手机号和验证码');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      <Card
        style={{
          width: 400,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}
        title={
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ margin: 0, color: '#1890ff' }}>甄来电管理系统</h2>
            <p style={{ margin: '8px 0 0', color: '#666', fontSize: '14px' }}>
              管理员登录
            </p>
          </div>
        }
      >
        <Tabs
          defaultActiveKey="password"
          centered
          items={[
            {
              key: 'password',
              label: '密码登录',
              children: (
                <Form
                  form={passwordForm}
                  layout="vertical"
                  onFinish={handlePasswordLogin}
                  autoComplete="off"
                >
                  <Form.Item
                    name="username"
                    label="用户名/手机号"
                    rules={[{ required: true, message: '请输入用户名或手机号' }]}
                  >
                    <Input
                      prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                      placeholder="请输入用户名或手机号"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    name="password"
                    label="密码"
                    rules={[{ required: true, message: '请输入密码' }]}
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                      placeholder="请输入密码"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item>
                    <Form.Item name="remember" valuePropName="checked" noStyle>
                      <Checkbox>记住我</Checkbox>
                    </Form.Item>
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      size="large"
                      block
                    >
                      登录
                    </Button>
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: 'sms',
              label: '验证码登录',
              children: (
                <Form
                  form={phoneForm}
                  layout="vertical"
                  onFinish={handlePhoneLogin}
                  autoComplete="off"
                >
                  <Form.Item
                    name="phone"
                    label="手机号"
                    rules={[
                      { required: true, message: '请输入手机号' },
                      { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }
                    ]}
                  >
                    <Input
                      prefix={<MobileOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                      placeholder="请输入手机号"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    name="smsCode"
                    label="验证码"
                    rules={[{ required: true, message: '请输入验证码' }]}
                  >
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Input
                        prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                        placeholder="请输入验证码"
                        size="large"
                        maxLength={6}
                        style={{ flex: 1 }}
                      />
                      <Button
                        onClick={handleSendSms}
                        loading={smsLoading}
                        disabled={smsCountdown > 0}
                        size="large"
                        style={{ minWidth: '120px' }}
                      >
                        {smsCountdown > 0 ? `${smsCountdown}s` : '发送验证码'}
                      </Button>
                    </div>
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      size="large"
                      block
                    >
                      登录
                    </Button>
                  </Form.Item>
                </Form>
              ),
            },
          ]}
        />

        {/* <div style={{ textAlign: 'center', marginTop: '16px', color: '#999', fontSize: '12px' }}>
          默认账号: 13800138000 / admin123456
        </div> */}
      </Card>
    </div>
  );
};

export default Login;